// "Traer de Mercado Libre": grilla de publicaciones seleccionables (mismo look que la de
// Tiendanube en Agregar varios), con la opción de vincular una publicación a un producto que
// ya está en Atlas en vez de crearlo de nuevo — así no se duplican los que se cargaron a mano.
// El listado y el import los hace atlas-backend; el import corre en el worker, así que cerrar
// esto no lo corta (ADR-2026-09-28-mercadolibre-importar-por-el-atributo-de-la-foto).

import React from "react";
import { createPortal } from "react-dom";
import { Check, Loader2, Package, X } from "lucide-react";
import { supabase } from "../lib/supabase";
import { ErrorDeMercadoLibre } from "../lib/mercadoLibreConexionLogica";
import { esperarImportDeMl, importarDeMl, listarPublicacionesDeMl } from "../lib/mercadoLibreCatalogo";
import {
  claveDePublicacion,
  idsDeLaSeleccion,
  MAX_POR_IMPORT,
  resumenDelImport,
  type ListadoDeMl,
  type PublicacionDeMl,
} from "../lib/mercadoLibreCatalogoLogica";
import { MercadoLibreIcon } from "./MercadoLibreIcon";

interface Props {
  open: boolean;
  companyId: string | null;
  brandId: string | null;
  onClose: () => void;
  /** Refresca la grilla de productos cuando terminan de entrar. */
  onSaved?: () => Promise<void> | void;
  zIndex?: number;
}

type Fase = "cargando" | "elegir" | "trayendo" | "listo" | "error";
type ProductoDeAtlas = { id: string; nombre: string };

/** Los códigos que se arreglan conectando (o reconectando) la cuenta desde Mi marca. */
const PIDE_CONECTAR = new Set(["sin_conexion", "reconectar"]);

export const MercadoLibreImportModal: React.FC<Props> = ({ open, companyId, brandId, onClose, onSaved, zIndex = 90 }) => {
  const [fase, setFase] = React.useState<Fase>("cargando");
  const [estado, setEstado] = React.useState<"active" | "paused">("active");
  const [listado, setListado] = React.useState<ListadoDeMl | null>(null);
  const [elegidas, setElegidas] = React.useState<Set<string>>(new Set());
  const [vinculos, setVinculos] = React.useState<Record<string, string>>({});
  const [productos, setProductos] = React.useState<ProductoDeAtlas[]>([]);
  const [error, setError] = React.useState<{ code: string; message: string } | null>(null);
  const [resumen, setResumen] = React.useState<{ texto: string; hayProblemas: boolean } | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);

  // Lista de Mercado Libre, cada vez que se abre o cambia de pestaña.
  React.useEffect(() => {
    if (!open) return;
    let cancelado = false;
    setFase("cargando");
    setError(null);
    setElegidas(new Set());
    setVinculos({});
    listarPublicacionesDeMl(brandId, estado)
      .then((l) => {
        if (cancelado) return;
        setListado(l);
        setFase("elegir");
      })
      .catch((e) => {
        if (cancelado) return;
        const code = e instanceof ErrorDeMercadoLibre ? e.code : "error";
        setError({ code, message: e instanceof Error ? e.message : "No pudimos leer tus publicaciones." });
        setFase("error");
      });
    return () => {
      cancelado = true;
    };
  }, [open, brandId, estado]);

  // Productos de la marca, para "Ya lo tengo en Atlas".
  React.useEffect(() => {
    if (!open || !companyId) return;
    let cancelado = false;
    let q = supabase.from("productos").select("id, nombre").eq("id_company", companyId).order("nombre").limit(500);
    if (brandId) q = q.eq("brand_id", brandId);
    void q.then(({ data }) => {
      if (!cancelado) setProductos(((data ?? []) as ProductoDeAtlas[]).filter((p) => p.nombre));
    });
    return () => {
      cancelado = true;
    };
  }, [open, companyId, brandId]);

  // Al cerrar se deja de seguir el job (el import sigue en el servidor).
  React.useEffect(() => {
    if (!open) abortRef.current?.abort();
  }, [open]);

  if (!open) return null;

  const publicaciones = listado?.publicaciones ?? [];
  const disponibles = publicaciones.filter((p) => !p.producto_id);
  const cantidadIds = idsDeLaSeleccion(publicaciones, elegidas);
  const pasado = cantidadIds > MAX_POR_IMPORT;

  const alternar = (p: PublicacionDeMl) => {
    if (p.producto_id || fase !== "elegir") return;
    const k = claveDePublicacion(p);
    setElegidas((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  };

  const todasElegidas = disponibles.length > 0 && disponibles.every((p) => elegidas.has(claveDePublicacion(p)));
  const alternarTodas = () =>
    setElegidas(todasElegidas ? new Set() : new Set(disponibles.map(claveDePublicacion)));

  const traer = async () => {
    if (elegidas.size === 0 || pasado) return;
    const pedido = publicaciones
      .filter((p) => elegidas.has(claveDePublicacion(p)))
      .map((p) => ({ item_ids: p.item_ids, producto_id: vinculos[claveDePublicacion(p)] || null }));
    setFase("trayendo");
    setError(null);
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const id = await importarDeMl(brandId, pedido);
      const r = await esperarImportDeMl(id, ac.signal);
      if (ac.signal.aborted) return;
      setResumen(resumenDelImport(r));
      setFase("listo");
      await onSaved?.();
    } catch (e) {
      if (ac.signal.aborted) return;
      const code = e instanceof ErrorDeMercadoLibre ? e.code : "error";
      setError({ code, message: e instanceof Error ? e.message : "No se pudo traer." });
      setFase("error");
    }
  };

  const irAConectar = () => {
    onClose();
    window.location.assign("/mi-marca?conectar=mercadolibre");
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-[min(640px,calc(100vw-32px))] max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[20px] font-semibold text-brand-black/90 inline-flex items-center gap-2">
              <MercadoLibreIcon className="h-5 w-5" />
              Traer de Mercado Libre
            </h2>
            <p className="text-[13px] text-brand-black/50 mt-1">
              Elegí las publicaciones que querés tener en Atlas. Cada color queda como un producto
              para generar fotos; talles, precio y stock se quedan en Mercado Libre.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Pestañas + seleccionar todas */}
        {(fase === "elegir" || fase === "cargando") && (
          <div className="px-6 pb-2 flex items-center justify-between gap-3">
            <div className="inline-flex rounded-lg bg-black/[0.05] p-0.5 text-[13px]">
              {(["active", "paused"] as const).map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEstado(e)}
                  disabled={fase === "cargando"}
                  className={`px-3 h-8 rounded-md font-medium transition-colors ${
                    estado === e ? "bg-white shadow-sm text-brand-black/85" : "text-brand-black/50"
                  }`}
                >
                  {e === "active" ? "Activas" : "Pausadas"}
                </button>
              ))}
            </div>
            {fase === "elegir" && disponibles.length > 0 && (
              <button
                type="button"
                onClick={alternarTodas}
                className="text-[13px] font-medium text-brand-primary hover:opacity-70"
              >
                {todasElegidas ? "Deseleccionar todas" : "Seleccionar todas"}
              </button>
            )}
          </div>
        )}

        {/* Cuerpo */}
        <div className="flex-1 overflow-y-auto px-6 pb-3">
          {fase === "cargando" && (
            <div className="flex flex-col items-center justify-center py-14 text-brand-black/45">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <p className="text-[14px]">Leyendo tus publicaciones…</p>
            </div>
          )}

          {fase === "error" && error && (
            <div className="flex flex-col items-center justify-center text-center py-12 gap-3">
              <p className="text-[14px] text-brand-black/70 max-w-sm">{error.message}</p>
              {PIDE_CONECTAR.has(error.code) && (
                <button
                  type="button"
                  onClick={irAConectar}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white font-medium text-[length:var(--text-body)] shadow-sm"
                >
                  {error.code === "reconectar" ? "Reconectar Mercado Libre" : "Conectar Mercado Libre"}
                </button>
              )}
            </div>
          )}

          {fase === "elegir" && publicaciones.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center py-12 text-brand-black/40">
              <Package className="w-7 h-7 mb-2" strokeWidth={1.5} />
              <p className="text-[14px]">
                {estado === "active" ? "No tenés publicaciones activas." : "No tenés publicaciones pausadas."}
              </p>
            </div>
          )}

          {fase === "elegir" && publicaciones.length > 0 && (
            <>
              {listado?.truncado && (
                <p className="text-[12.5px] text-brand-black/45 mb-2">
                  Te mostramos las {publicaciones.length} más recientes de {listado.total}.
                </p>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {publicaciones.map((p) => (
                  <TarjetaDePublicacion
                    key={claveDePublicacion(p)}
                    p={p}
                    elegida={elegidas.has(claveDePublicacion(p))}
                    onClick={() => alternar(p)}
                  />
                ))}
              </div>

              {/* Vincular en vez de crear: sólo para las elegidas, y sólo si hay productos. */}
              {elegidas.size > 0 && productos.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-[12.5px] font-medium text-brand-black/50">
                    ¿Alguna ya la tenés cargada en Atlas? Vinculala y no se duplica.
                  </p>
                  {publicaciones
                    .filter((p) => elegidas.has(claveDePublicacion(p)))
                    .map((p) => {
                      const k = claveDePublicacion(p);
                      return (
                        <label key={k} className="flex items-center gap-2 text-[13px] text-brand-black/70">
                          <span className="flex-1 min-w-0 truncate">{p.titulo}</span>
                          <select
                            value={vinculos[k] ?? ""}
                            onChange={(e) => setVinculos((v) => ({ ...v, [k]: e.target.value }))}
                            className="max-w-[55%] h-8 rounded-md border border-neutral-200 bg-white px-2 text-[13px] text-brand-black/80 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none"
                          >
                            <option value="">Crear producto nuevo</option>
                            {productos.map((x) => (
                              <option key={x.id} value={x.id}>
                                Es «{x.nombre}»
                              </option>
                            ))}
                          </select>
                        </label>
                      );
                    })}
                </div>
              )}
            </>
          )}

          {fase === "trayendo" && (
            <div className="flex flex-col items-center justify-center text-center py-14 text-brand-black/55 gap-2">
              <Loader2 className="w-6 h-6 animate-spin" />
              <p className="text-[14px]">Trayendo tus productos…</p>
              <p className="text-[12.5px] text-brand-black/40 max-w-xs">
                Puede tardar unos minutos. Si cerrás esta ventana, se siguen sumando solos.
              </p>
            </div>
          )}

          {fase === "listo" && resumen && (
            <div className="flex flex-col items-center justify-center text-center py-12 gap-2">
              <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center">
                <Check className="w-5 h-5 text-brand-primary" />
              </div>
              <p className={`text-[14px] max-w-sm ${resumen.hayProblemas ? "text-amber-700" : "text-brand-black/70"}`}>
                {resumen.texto}
              </p>
            </div>
          )}
        </div>

        {/* Pie */}
        <div className="px-6 py-4 flex items-center justify-end gap-3 border-t border-neutral-100">
          {pasado && (
            <span className="mr-auto text-[12.5px] text-error">Hasta {MAX_POR_IMPORT} publicaciones por vez.</span>
          )}
          {fase === "elegir" ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="h-[44px] px-3 text-[14px] font-medium text-brand-black/55 hover:text-brand-black/90 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={traer}
                disabled={elegidas.size === 0 || pasado}
                className="inline-flex items-center justify-center gap-2 h-[44px] px-6 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {elegidas.size === 0 ? "Traer" : `Traer ${elegidas.size}`}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center h-[44px] px-6 rounded-md border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100 text-[15px] font-medium shadow-sm transition-colors"
            >
              {fase === "trayendo" ? "Seguir en segundo plano" : "Cerrar"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const TarjetaDePublicacion: React.FC<{ p: PublicacionDeMl; elegida: boolean; onClick: () => void }> = ({
  p,
  elegida,
  onClick,
}) => {
  const yaEsta = !!p.producto_id;
  const detalle = yaEsta
    ? "Ya está en Atlas"
    : p.colores.length > 1
      ? `${p.colores.length} colores`
      : p.combinaciones > 1
        ? `${p.combinaciones} variantes`
        : null;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={yaEsta}
      title={p.titulo}
      className={`group text-left rounded-xl overflow-hidden border-2 transition-colors disabled:cursor-default ${
        elegida ? "border-brand-primary" : "border-transparent hover:border-neutral-200"
      }`}
    >
      <div className="relative aspect-square bg-neutral-100">
        {p.miniatura ? (
          <img src={p.miniatura} alt="" loading="lazy" className={`absolute inset-0 w-full h-full object-cover ${yaEsta ? "opacity-50" : ""}`} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Package className="w-5 h-5 text-brand-black/25" strokeWidth={1.5} />
          </div>
        )}
        {elegida && <div className="absolute inset-0 bg-brand-primary/15 pointer-events-none" />}
        {!yaEsta && (
          <span
            className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full inline-flex items-center justify-center transition-colors ${
              elegida ? "bg-brand-primary text-white" : "bg-white/85 border border-black/10 text-transparent"
            }`}
          >
            <Check className="w-3 h-3" />
          </span>
        )}
        {p.es_catalogo && (
          <span className="absolute bottom-1.5 left-1.5 text-[10px] font-medium bg-black/60 text-white rounded px-1.5 py-0.5">
            Catálogo
          </span>
        )}
      </div>
      <div className="px-1.5 py-1.5">
        <span className="block text-[12.5px] leading-tight text-brand-black/80 line-clamp-2">{p.titulo}</span>
        {detalle && <span className="block text-[11.5px] text-brand-black/45 mt-0.5">{detalle}</span>}
      </div>
    </button>
  );
};

export default MercadoLibreImportModal;
